import { Sparkles, Minimize2, Maximize2, Radio, Download, X, ChevronDown } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import botIcon from '../../../../../assets/Chatbot.svg';
import type { SupportedLanguage } from '../../services/chatService';

interface ChatHeaderProps {
  title?: string;
  subtitle?: string;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  streamingMode?: boolean;
  onToggleStreaming?: () => void;
  onToggleExport?: () => void;
  isExportMode?: boolean;
  selectedLanguage?: SupportedLanguage;
  onLanguageChange?: (language: SupportedLanguage) => void;
  enableGovAPI?: boolean;
  allLanguages?: SupportedLanguage[];
  languageDisplay?: Partial<Record<SupportedLanguage, { name: string; native: string }>>;
}

export const ChatHeader = ({
  title = 'AI Assistant',
  subtitle = 'Online',
  isExpanded = false,
  onToggleExpand,
  streamingMode = false,
  onToggleStreaming,
  onToggleExport,
  isExportMode = false,
  selectedLanguage = 'english',
  onLanguageChange,
  enableGovAPI = true,
  allLanguages = [],
  languageDisplay = {}
}: ChatHeaderProps) => {
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLanguageDropdownOpen(false);
      }
    };

    if (isLanguageDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLanguageDropdownOpen]);

  const handleLanguageSelect = (language: SupportedLanguage) => {
    onLanguageChange?.(language);
    setIsLanguageDropdownOpen(false);
  };

  const currentLanguageDisplay = languageDisplay?.[selectedLanguage] || { name: selectedLanguage.charAt(0).toUpperCase() + selectedLanguage.slice(1), native: selectedLanguage.charAt(0).toUpperCase() + selectedLanguage.slice(1) };

  return (
    <div className="bg-white border-b border-gray-100 px-6 py-5 shadow-sm">
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#4c3794] to-[#3a2a73] flex items-center justify-center shadow-lg shadow-purple-300/50">
              {/* <Sparkles className="w-6 h-6 text-white" strokeWidth={2.5} /> */}
              <img src={botIcon} alt="bot" className="w-11 h-11 rounded-2xl" />
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white shadow-sm animate-pulse" />
          </div>
          <div>
            <h3 className="text-gray-900 font-bold text-base tracking-tight">{title}</h3>
            <p className="text-xs text-gray-500 mt-0.5 font-medium">{subtitle}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Language Dropdown */}
          {enableGovAPI && allLanguages.length > 0 && onLanguageChange && (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className="flex items-center gap-2 px-3.5 py-2 text-sm font-semibold text-[#4c3794] bg-white border border-gray-200 rounded-xl hover:bg-purple-50 hover:border-[#4c3794] transition-all shadow-sm hover:shadow active:scale-95"
                aria-label="Select language"
              >
                <span className="text-xs">{currentLanguageDisplay.native}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isLanguageDropdownOpen ? 'rotate-180' : ''}`} strokeWidth={2.5} />
              </button>

              {isLanguageDropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
                  <div className="p-2 language-dropdown-scroll" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                    {/* Global Languages Section */}
                    <div className="mb-2">
                      <div className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                        Global
                      </div>
                      {allLanguages.slice(0, 10).map((lang) => {
                        const display = languageDisplay?.[lang] || { name: lang.charAt(0).toUpperCase() + lang.slice(1), native: lang.charAt(0).toUpperCase() + lang.slice(1) };
                        const isSelected = lang === selectedLanguage;
                        return (
                          <button
                            key={lang}
                            onClick={() => handleLanguageSelect(lang)}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                              isSelected
                                ? 'bg-[#4c3794] text-white font-semibold'
                                : 'text-gray-700 hover:bg-purple-50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span>{display.native}</span>
                              <span className={`text-xs ${isSelected ? 'text-white/80' : 'text-gray-500'}`}>
                                {display.name}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    
                    {/* Indian Languages Section */}
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <div className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                        Indian
                      </div>
                      {allLanguages.slice(10).map((lang) => {
                        const display = languageDisplay?.[lang] || { name: lang.charAt(0).toUpperCase() + lang.slice(1), native: lang.charAt(0).toUpperCase() + lang.slice(1) };
                        const isSelected = lang === selectedLanguage;
                        return (
                          <button
                            key={lang}
                            onClick={() => handleLanguageSelect(lang)}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                              isSelected
                                ? 'bg-[#4c3794] text-white font-semibold'
                                : 'text-gray-700 hover:bg-purple-50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span>{display.native}</span>
                              <span className={`text-xs ${isSelected ? 'text-white/80' : 'text-gray-500'}`}>
                                {display.name}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {onToggleExport && (
            <button
              onClick={onToggleExport}
              className="flex items-center gap-2 px-3.5 py-2 text-sm font-semibold text-[#4c3794] bg-white border border-gray-200 rounded-xl hover:bg-purple-50 hover:border-[#4c3794] transition-all shadow-sm hover:shadow active:scale-95"
              aria-label="Export content"
            >
              <Download className="w-4 h-4" strokeWidth={2.5} />
              <span>Export</span>
            </button>
          )}

          {onToggleStreaming && (
            <button
              onClick={onToggleStreaming}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all shadow-sm hover:shadow active:scale-95 ${
                streamingMode
                  ? 'bg-[#4c3794] text-white hover:bg-[#3a2a73] shadow-purple-300/50'
                  : 'bg-white text-[#4c3794] border border-gray-200 hover:bg-purple-50 hover:border-[#4c3794]'
              }`}
              aria-label={streamingMode ? 'End conversation' : 'Start voice conversation'}
            >
              <Radio className={`w-4 h-4 ${streamingMode ? 'animate-pulse' : ''}`} strokeWidth={2.5} />
              <span>{streamingMode ? 'End' : 'Voice'}</span>
            </button>
          )}

         
        </div>
      </div>
    </div>
  );
};
